/**
 * One handler per effect type in `effectSchema` (`@ab/content-schema`). Skills,
 * items and passives all apply their effects through `applyEffect`. Formulas
 * are on the wiki page Design → Combat rules; the order a skill resolves in is
 * on Architecture → game-core contract, section Skill resolution.
 */
import type { Effect, Element } from '@ab/content-schema';
import { damageFormula, dealDamage, gainExp, maxAp, maxHp, stabPercent, typePercent } from './combat.ts';
import { type ApplyResult, type Ctx, none } from './commands/types.ts';
import { idiv, percent } from './determinism.ts';
import type { Event, HealCause } from './events.ts';
import { gridView } from './floorview.ts';
import { type Pos, type ResolvedTarget, add, dirOffset, dirTo, inBounds, manhattan, orthogonalDirs, samePos } from './grid.ts';
import { type Style, healingReceivedMod } from './passives.ts';
import { roll } from './rng.ts';
import { InvalidCommand, type RunState, type Unit, findUnit, updateUnit, withRng } from './state.ts';
import { effectiveStat, typesOf } from './stats.ts';
import { addStage, applyStatus, cureStatus, interruptCharge } from './status.ts';

/** Who applies an effect, and how a `damage` effect is calculated. */
export type EffectSource = {
  /** The acting unit: the skill or item user, or the passive holder. */
  readonly unit: string;
  /**
   * Chooses patk/pdef or aatk/adef for a `damage` effect that names no `style`
   * of its own: the skill's style; null for items and passives, whose `damage`
   * effects must name one (content lint enforces it).
   */
  readonly style: Style | null;
  /** The skill's element, used when a `damage` effect names none. */
  readonly element: Element | null;
  /** Power points lost per tile beyond the first along a `line` (`falloff.power`). */
  readonly falloffPower: number;
  /** What applies the effect; a `heal` from a passive emits `healed` with cause `passive`, else `effect`. */
  readonly origin: 'skill' | 'item' | 'passive';
};

/** A fixed amount, or `max × num ÷ den` (minimum 1). */
type HpQuantity = { readonly amount: number } | { readonly num: number; readonly den: number };

const quantity = (q: HpQuantity, max: number): number =>
  'amount' in q ? q.amount : Math.max(1, idiv(max * q.num, q.den));

/**
 * Effects game-core refuses to run from a skill or an item's use: `custom`
 * (not modelled), `revive` (only a charm's `auto` block revives, and it never
 * goes through here), and `damage` with no style of its own when the source
 * has none either (`style` is the skill's style, null for items).
 */
export const unrunnable = (effects: readonly Effect[], style: Style | null): string | null => {
  for (const e of effects) {
    if (e.type === 'custom') return e.key;
    if (e.type === 'revive') return 'revive outside a charm';
    if (e.type === 'damage' && e.style === undefined && style === null) return 'damage without a style';
  }
  return null;
};

/** Throws `InvalidCommand('effect not modelled: <key>')` if any effect cannot run. */
export const assertRunnable = (effects: readonly Effect[], style: Style | null): void => {
  const key = unrunnable(effects, style);
  if (key !== null) throw new InvalidCommand('effect_not_modelled', `effect not modelled: ${key}`, { effect: key });
};

const escapeRefused = 'escape can only be used by the leader on itself';

/**
 * An item or skill with an `escape` effect may be used only by the leader,
 * with the leader as its one and only target. Checked with the other checks
 * of a skill or item use, before the round starts, so an escape is never
 * refused mid-round. Throws `InvalidCommand` otherwise.
 */
export const assertEscapeAllowed = (state: RunState, userId: string, targets: readonly ResolvedTarget[], effects: readonly Effect[]): void => {
  if (!effects.some((e) => e.type === 'escape')) return;
  const onlyTarget = targets.length === 1 ? targets[0] : undefined;
  if (userId !== state.party[0] || onlyTarget?.unit !== userId) throw new InvalidCommand('escape_leader_only', escapeRefused);
};

/**
 * A skill or item with a `heal` effect is refused when it would heal nobody:
 * when every effect is a `heal` or a `restore_ap` and no resolved target is
 * below its max HP with a heal that comes to more than 0 after its
 * `healing_received_mod` (`healAmount`; a −100 modifier heals nothing) (or below its max AP, for a `restore_ap` in the same use,
 * so an Elixir still restores a full-HP unit's AP). A use with any other
 * effect is never refused here. Checked with the other checks of a skill or
 * item use, before any cost is paid. Throws `InvalidCommand('nobody to heal')`.
 */
export const assertSomeoneToHeal = (ctx: Ctx, state: RunState, targets: readonly ResolvedTarget[], effects: readonly Effect[]): void => {
  if (!effects.some((e) => e.type === 'heal')) return;
  if (!effects.every((e) => e.type === 'heal' || e.type === 'restore_ap')) return;
  const restoresAp = effects.some((e) => e.type === 'restore_ap');
  const gains = targets.some((rt) => {
    const u = findUnit(state, rt.unit);
    if (u === null || u.down) return false;
    const healsHp = u.hp < maxHp(ctx, u) && effects.some((e) => e.type === 'heal' && healAmount(ctx, u, e.hp) > 0);
    return healsHp || (restoresAp && u.ap < maxAp(ctx, u));
  });
  if (!gains) throw new InvalidCommand('nobody_to_heal', 'nobody to heal');
};

/**
 * The leader leaves the dungeon: phase `escaped` (terminal), enemies and the
 * floor cleared as by `descend`; the party, bag, levels and exp are kept.
 * Emits `escaped` then `run_escaped`. The round stops after this turn (round.ts).
 */
const escape = (state: RunState, userId: string, targetId: string): ApplyResult => {
  if (userId !== state.party[0] || targetId !== userId) throw new InvalidCommand('escape_leader_only', escapeRefused);
  return {
    state: { ...state, phase: 'escaped', units: state.units.filter((u) => u.side === 'party'), floor: null },
    events: [
      { type: 'escaped', unit: userId },
      { type: 'run_escaped' },
    ],
  };
};

const damage = (ctx: Ctx, state: RunState, effect: Extract<Effect, { type: 'damage' }>, source: EffectSource, target: ResolvedTarget): ApplyResult => {
  const attacker = findUnit(state, source.unit);
  const defender = findUnit(state, target.unit);
  if (attacker === null || defender === null || defender.down) return none(state);
  // The effect's own style wins; else the skill's. Content lint requires one on item and passive damage.
  const style = effect.style ?? source.style;
  if (style === null) throw new InvalidCommand('damage_without_style', 'effect not modelled: damage without a style');
  const element = effect.element ?? source.element;
  const physical = style === 'physical';
  const amount = damageFormula({
    level: attacker.level,
    power: Math.max(1, effect.power - source.falloffPower * target.distance),
    atk: effectiveStat(ctx.content, attacker, physical ? 'patk' : 'aatk'),
    def: effectiveStat(ctx.content, defender, physical ? 'pdef' : 'adef'),
    typePct: typePercent(ctx, element, typesOf(ctx.content, defender)),
    stabPct: stabPercent(ctx, element, typesOf(ctx.content, attacker)),
  });
  return dealDamage(ctx, state, target.unit, amount, source.unit, element, style);
};

/** What a heal of `hp` gives `u` before the cap: the amount or fraction of its max HP, × its `healing_received_mod` (minimum 0). */
const healAmount = (ctx: Ctx, u: Unit, hp: HpQuantity): number => {
  const mod = healingReceivedMod(ctx, u);
  const base = quantity(hp, maxHp(ctx, u));
  return mod.passive !== null ? Math.max(0, percent(base, 100 + mod.sum)) : base;
};

/**
 * Heals a standing unit by the amount or fraction of its max HP, scaled by its
 * `healing_received_mod` passives (checked before the heal), capped at max HP.
 * `healed` carries `cause`: what healed (a heal effect, sleep, regeneration, a passive).
 */
const heal = (ctx: Ctx, state: RunState, effect: Extract<Effect, { type: 'heal' }>, target: ResolvedTarget, cause: HealCause): ApplyResult => {
  const u = findUnit(state, target.unit);
  if (u === null || u.down) return none(state);
  const max = maxHp(ctx, u);
  const mod = healingReceivedMod(ctx, u);
  const hp = Math.min(max, u.hp + healAmount(ctx, u, effect.hp));
  const events: Event[] = [];
  if (mod.passive !== null) events.push({ type: 'passive_triggered', unit: u.id, passive: mod.passive, cause });
  events.push({ type: 'healed', unit: u.id, amount: hp - u.hp, hp, cause });
  return { state: updateUnit(state, u.id, (x) => ({ ...x, hp })), events };
};

/**
 * Heals a standing unit by `hp` (an amount, or a fraction of its max HP with minimum 1) exactly
 * as a `heal` effect does, `healing_received_mod` included. Used by the rules
 * that heal without a skill: sleep and natural regeneration (round.ts), which
 * pass their `cause`.
 */
export const healQuantity = (
  ctx: Ctx,
  state: RunState,
  unitId: string,
  hp: { readonly amount: number } | { readonly num: number; readonly den: number },
  cause: 'sleep' | 'regen',
): ApplyResult => {
  const u = findUnit(state, unitId);
  if (u === null || u.down) return none(state);
  return heal(ctx, state, { type: 'heal', hp }, { unit: unitId, pos: u.pos, distance: 0 }, cause);
};

const restoreAp = (ctx: Ctx, state: RunState, effect: Extract<Effect, { type: 'restore_ap' }>, target: ResolvedTarget): ApplyResult => {
  const u = findUnit(state, target.unit);
  if (u === null || u.down) return none(state);
  const max = maxAp(ctx, u);
  const ap = Math.min(max, u.ap + quantity(effect.ap, max));
  return {
    state: updateUnit(state, u.id, (x) => ({ ...x, ap })),
    events: [{ type: 'ap_changed', unit: u.id, delta: ap - u.ap, ap }],
  };
};

/**
 * A down party unit stands again (a charm's `revive`, combat.ts
 * `fallCharm`): `down` false, hp the amount or fraction of its max HP
 * (minimum 1, capped at max HP; no `healing_received_mod`: it is not a heal),
 * no statuses, stat stages or charge, good or bad, whatever it held (decided
 * 2026-09-26, Q36; the fall has already cleared them, and the revive
 * guarantees it for any caller), `firedOnce` emptied (being revived resets
 * once-per-battle passives) and `restTurns` 0; its tile, facing, AP and SP are
 * kept. `revived` with the hp. A unit that is gone or standing is left alone.
 */
const revive = (ctx: Ctx, state: RunState, effect: Extract<Effect, { type: 'revive' }>, target: ResolvedTarget): ApplyResult => {
  const u = findUnit(state, target.unit);
  if (u === null || !u.down) return none(state);
  const max = maxHp(ctx, u);
  const hp = Math.min(max, quantity(effect.hp, max));
  return {
    state: updateUnit(state, u.id, (x) => ({ ...x, down: false, hp, statuses: [], stages: [], charging: null, firedOnce: [], restTurns: 0 })),
    events: [{ type: 'revived', unit: u.id, hp }],
  };
};

/**
 * The user moves to a free tile among the four orthogonal neighbours of the
 * target's tile (walkable: floor or exit, and unoccupied), the one nearest
 * (Manhattan) to where the user stands, ties by direction order n e s w, and
 * faces the target's tile. No move when the user already stands on one of
 * those four tiles or none is free. Not a step: no corner rule, no pickup.
 */
const reposition = (state: RunState, userId: string, targetPos: Pos): ApplyResult => {
  const u = findUnit(state, userId);
  if (u === null || u.down || samePos(u.pos, targetPos) || manhattan(u.pos, targetPos) === 1) return none(state);
  const g = gridView(state);
  let best: Pos | null = null;
  for (const d of orthogonalDirs) {
    const p = add(targetPos, dirOffset[d]);
    if (!inBounds(g, p) || !g.isFloor(p) || g.unitAt(p) !== null) continue;
    if (best === null || manhattan(u.pos, p) < manhattan(u.pos, best)) best = p;
  }
  if (best === null) return none(state);
  const to = best;
  const facing = dirTo(to, targetPos) ?? u.facing;
  const events: Event[] = [{ type: 'moved', unit: userId, from: u.pos, to }];
  if (facing !== u.facing) events.push({ type: 'turned', unit: userId, facing });
  return { state: updateUnit(state, userId, (x) => ({ ...x, pos: to, facing })), events };
};

/**
 * Applies one effect from `source` to one resolved target. Accuracy has
 * already been rolled by the caller. Once the run has left the floor (an
 * `escape` earlier in the same use), the remaining effects do nothing.
 */
export const applyEffect = (ctx: Ctx, state: RunState, effect: Effect, source: EffectSource, target: ResolvedTarget): ApplyResult => {
  if (state.phase !== 'playing') return none(state);
  switch (effect.type) {
    case 'damage':
      return damage(ctx, state, effect, source, target);
    case 'heal':
      return heal(ctx, state, effect, target, source.origin === 'passive' ? 'passive' : 'effect');
    case 'restore_ap':
      return restoreAp(ctx, state, effect, target);
    case 'status': {
      const u = findUnit(state, target.unit);
      if (u === null || u.down) return none(state);
      const r = roll(state.rng.combat, effect.chance);
      const s = withRng(state, 'combat', r.next);
      return r.hit ? applyStatus(s, target.unit, effect.status, effect.turns) : none(s);
    }
    case 'cure':
      return cureStatus(state, target.unit, effect.statuses);
    case 'stat_stage':
      return addStage(state, target.unit, effect.stat, effect.stages, effect.turns ?? null);
    case 'exp': {
      const u = findUnit(state, target.unit);
      return u === null || u.down ? none(state) : gainExp(ctx, state, target.unit, effect.amount);
    }
    case 'escape':
      return escape(state, source.unit, target.unit);
    case 'interrupt_charge':
      return interruptCharge(state, target.unit);
    case 'reposition':
      return reposition(state, source.unit, target.pos);
    case 'revive':
      return revive(ctx, state, effect, target);
    case 'custom':
      throw new InvalidCommand('effect_not_modelled', `effect not modelled: ${effect.key}`, { effect: effect.key });
  }
};
